import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Button as MuiButton } from '@material-ui/core'
import clsx from 'clsx'
import { bool, func, node, oneOf } from 'prop-types'

const useStyles = makeStyles(
	(theme) => {
		const { palette } = theme
		return {
			Button: {
				alignItems: 'center',
				background: palette.secondary.main,
				border: `2px solid ${palette.secondary.main}`,
				color: palette.tertiary.dark,
				cursor: 'pointer',
				display: 'flex',
				fontSize: '0.8rem',
				justifyContent: 'center',
				letterSpacing: '2.4px',
				lineHeight: '1rem',
				maxWidth: '290px',
				padding: '15px 8px',
				textAlign: 'center',
				textTransform: 'uppercase',
				transition: 'all 0.4s ease',
				width: '100%',
				'&:disabled': {
					backgroundColor: palette.grey[300],
					borderColor: palette.grey[300],
					cursor: 'not-allowed',
					transition: 'none',
					'&:hover': {
						backgroundColor: palette.grey[300],
						borderColor: palette.grey[300],
						color: palette.tertiary.dark,
					},
				},
				'&:hover': {
					background: palette.tertiary.main,
					borderColor: palette.tertiary.main,
					color: palette.common.white,
				},

				'&.primary': {
					background: palette.secondary.main,
					borderColor: palette.secondary.main,
					color: palette.tertiary.dark,
					'&:disabled': {
						'&:hover': {
							color: palette.tertiary.dark,
						},
					},
					'&:hover': {
						background: palette.tertiary.main,
						borderColor: palette.tertiary.main,
						color: palette.common.white,
					},
				},
				'&.secondary': {
					background: palette.common.white,
					borderColor: palette.secondary.main,
					color: palette.tertiary.dark,
					'&:disabled': {
						'&:hover': {
							color: palette.tertiary.dark,
						},
					},
					'&:hover': {
						background: palette.tertiary.main,
						borderColor: palette.tertiary.main,
						color: palette.common.white,
					},
				},
				'&.tertiary': {
					background: palette.old.green,
					borderColor: palette.old.green,
					color: palette.tertiary.dark,
					'&:disabled': {
						'&:hover': {
							color: palette.tertiary.dark,
						},
					},
					'&:hover': {
						background: palette.primary.main,
						borderColor: palette.primary.main,
						color: palette.common.white,
					},
				},
				'&.warning': {
					background: '#ec3a3a',
					borderColor: '#ec3a3a',
					color: palette.common.white,
					'&:disabled': {
						'&:hover': {
							color: palette.common.white,
						},
					},
					'&:hover': {
						background: palette.primary.main,
						borderColor: palette.primary.main,
						color: palette.common.white,
					},
				},
			},
		}
	},
	{ name: 'button' }
)

Button.propTypes = {
	/** context that lives inside the button component */
	children: node.isRequired,
	/** determines if the button should have a max-width of 290px (ex: 'primary', 'secondary', 'tertiary') */
	fullWidth: bool,
	/** function that executes when button is clicked */
	onClick: func,
	/** Determines how the button should display based on set variations */
	variant: oneOf(['primary', 'secondary', 'tertiary', 'warning']),
}

/**
 * Button Component
 *
 * Variant = 'primary' Primary = Mainly used type of button
 *
 * @param {Node} props.children :: context that lives inside the button component
 * @param {Boolean} props.fullWidth :: determines if the button should have a max-width of 290px (ex: 'primary', 'secondary', 'tertiary')
 * @param {Function} props.onClick :: function that executes when button is clicked
 * @param {String} props.variant = 'primary' :: determines how the button should display based on set variations
 */
export function Button({ children, className, fullWidth = false, onClick, variant = 'primary', ...restOfAttributes }) {
	const classes = useStyles()
	return (
		<MuiButton
			className={clsx(classes.Button, className, variant)}
			fullWidth={fullWidth}
			onClick={onClick}
			{...restOfAttributes}
		>
			{children}
		</MuiButton>
	)
}
